import { Link } from "@tanstack/react-router";
import { ChevronSmallRightIcon } from "@wags/ui/product-icons.tsx";
import { usePostHog } from "posthog-js/react";
import { DogPhoto } from "./dog-photo";
import { captureMoreDogsClicked } from "./more-dogs-analytics";
import type { OtherDog } from "./other-dogs";

const GRID_SIZE = 4;

export function MoreDogsGalleryEnd({
    title,
    dogs,
    availableOthers,
    providerId,
    sourceListingId,
}: {
    title: string;
    dogs: OtherDog[];
    availableOthers: number;
    providerId: string;
    sourceListingId: string;
}) {
    const posthog = usePostHog();
    const shown = dogs.slice(0, dogs.length >= GRID_SIZE ? GRID_SIZE : 2);
    const showSeeAll = availableOthers > 2;
    return (
        <div
            data-more-dogs-end
            className="flex h-full w-full items-center justify-center bg-doberman-black px-6 pt-18 pb-9 md:px-20 md:py-16"
        >
            <div data-more-dogs-end-column>
                <h2
                    data-more-dogs-end-title
                    className="break-words text-section-title text-true-white text-balance"
                >
                    {title}
                </h2>
                <ul data-more-dogs-end-grid>
                    {shown.map((dog, position) => (
                        <li key={dog.id}>
                            <Link
                                to="/dog/$dogId"
                                params={{ dogId: dog.id }}
                                onClick={() =>
                                    captureMoreDogsClicked(posthog, {
                                        kind: "dog",
                                        surface: "gallery_end",
                                        position,
                                        sourceListingId,
                                        targetListingId: dog.id,
                                    })
                                }
                                className="group relative block size-full overflow-hidden rounded-card bg-true-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-doberman-black"
                            >
                                <span className="block size-full transition-transform duration-300 ease-out group-active:scale-98 [@media(hover:hover)]:group-hover:scale-103 motion-reduce:transition-none">
                                    <DogPhoto
                                        url={dog.imageUrl}
                                        size={240}
                                        tone="dark"
                                        listingMedia
                                    />
                                </span>
                                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-true-black/75 via-true-black/30 to-transparent px-3 pt-10 pb-2.5">
                                    <span className="block truncate font-family-heading text-lg leading-tight text-true-white">
                                        {dog.name}
                                    </span>
                                    <span className="block truncate text-caption text-true-white/80">
                                        {[dog.breedName, dog.ageLabel]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </span>
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
                {showSeeAll && (
                    <Link
                        data-more-dogs-end-see-all-link
                        to="/provider/$providerId"
                        params={{ providerId }}
                        onClick={() =>
                            captureMoreDogsClicked(posthog, {
                                kind: "see_all",
                                surface: "gallery_end",
                                sourceListingId,
                            })
                        }
                        className="flex items-center justify-between gap-3 rounded-nested bg-true-white/10 px-4 py-3 text-label text-true-white md:py-3.5 transition-colors duration-150 [@media(hover:hover)]:hover:bg-true-white/15 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow"
                    >
                        See all {availableOthers.toLocaleString()} dogs
                        <ChevronSmallRightIcon
                            aria-hidden="true"
                            className="size-5 text-true-white/60"
                        />
                    </Link>
                )}
            </div>
        </div>
    );
}
